import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Api } from '../../utils/apiServer'
import { API } from '../../utils/api'

// Async thunks - Fetch operations (public, no auth needed)
export const fetchJobs = createAsyncThunk('jobs/fetchJobs', async (params) => {
    // Unified endpoint for both admin and public
    const apiParams = {
        url: API.JOBS_LIST.url,
        method: API.JOBS_LIST.type,
        reqBody: params || {}
    }
    const response = await Api(apiParams)
    // Return full response (data + pagination)
    return response || {}
})

export const fetchLocations = createAsyncThunk('jobs/fetchLocations', async () => {
    const params = {
        url: API.LOCATIONS_LIST_PUBLIC.url,
        method: API.LOCATIONS_LIST_PUBLIC.type,
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || response || []
})

export const fetchCities = createAsyncThunk('jobs/fetchCities', async () => {
    const params = {
        url: API.CITIES_LIST_PUBLIC.url,
        method: API.CITIES_LIST_PUBLIC.type,
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || response || []
})

export const fetchSkills = createAsyncThunk('jobs/fetchSkills', async () => {
    const params = {
        url: API.SKILLS_LIST_PUBLIC.url,
        method: API.SKILLS_LIST_PUBLIC.type,
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || response || []
})

export const fetchExperienceLevels = createAsyncThunk('jobs/fetchExperienceLevels', async () => {
    const params = {
        url: API.EXPERIENCE_LEVELS_LIST_PUBLIC.url,
        method: API.EXPERIENCE_LEVELS_LIST_PUBLIC.type,
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || response || []
})

export const fetchEducationLevels = createAsyncThunk('jobs/fetchEducationLevels', async () => {
    const params = {
        url: API.EDUCATION_LEVELS_LIST_PUBLIC.url,
        method: API.EDUCATION_LEVELS_LIST_PUBLIC.type,
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || response || []
})

// Write operations (session-based, cookies sent automatically)
export const createSkill = createAsyncThunk('jobs/createSkill', async (skillData) => {
    const params = {
        url: API.SKILLS_CREATE.url,
        method: API.SKILLS_CREATE.type,
        reqBody: skillData
    }
    const response = await Api(params)
    return response?.data || response || {}
})

export const updateSkill = createAsyncThunk('jobs/updateSkill', async (skillData) => {
    const params = {
        url: API.SKILLS_UPDATE.url + `/${skillData.id}`,
        method: API.SKILLS_UPDATE.type,
        reqBody: skillData
    }
    const response = await Api(params)
    return response?.data || response || {}
})

export const deleteSkill = createAsyncThunk('jobs/deleteSkill', async (skillId) => {
    const params = {
        url: API.SKILLS_DELETE.url + `/${skillId}`,
        method: API.SKILLS_DELETE.type,
        reqBody: {}
    }
    await Api(params)
    return skillId
})

export const createJob = createAsyncThunk('jobs/createJob', async (jobData) => {
    const params = {
        url: API.JOBS_CREATE.url,
        method: API.JOBS_CREATE.type,
        reqBody: jobData
    }
    const response = await Api(params)
    return response || {}
})

export const updateJob = createAsyncThunk('jobs/updateJob', async (jobData) => {
    const params = {
        url: API.JOBS_UPDATE.url + `/${jobData.id}`,
        method: API.JOBS_UPDATE.type,
        reqBody: jobData
    }
    const response = await Api(params)
    return response || {}
})

export const deleteJob = createAsyncThunk('jobs/deleteJob', async (jobId) => {
    const params = {
        url: API.JOBS_DELETE.url + `/${jobId}`,
        method: API.JOBS_DELETE.type,
        reqBody: {}
    }
    await Api(params)
    return jobId
})

export const fetchJobApplicants = createAsyncThunk('jobs/fetchJobApplicants', async ({ jobId, page = 1, rows = 20, search = '' }) => {
    const params = {
        url: `${API.JOB_APPLICANTS.url}/${jobId}/applicants`,
        method: 'get',
        reqBody: { page, rows, search }
    }
    const response = await Api(params)
    return response || {}
})

export const fetchInterviewByOutsourceIds = createAsyncThunk('jobs/fetchInterviewByOutsourceIds', async (outsourceIds) => {
    const params = {
        url: API.INTERVIEW_BY_OUTSOURCE.url,
        method: 'get',
        reqBody: { outsource_ids: outsourceIds.join(',') }
    }
    const response = await Api(params)
    return response?.data || {}
})

export const fetchInterviewDetail = createAsyncThunk('jobs/fetchInterviewDetail', async (interviewId) => {
    const params = {
        url: `${API.INTERVIEW_ADMIN_DETAIL.url}/${interviewId}`,
        method: 'get',
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || null
})

export const fetchJobInterviewSummary = createAsyncThunk('jobs/fetchJobInterviewSummary', async (jobId) => {
    const params = {
        url: `${API.INTERVIEW_JOB_SUMMARY.url}/${jobId}`,
        method: 'get',
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || null
})

export const fetchPositions = createAsyncThunk('jobs/fetchPositions', async () => {
    const params = {
        url: API.POSITIONS_LIST.url,
        method: API.POSITIONS_LIST.type,
        reqBody: {}
    }
    const response = await Api(params)
    return response?.data || response || []
})

export const createPosition = createAsyncThunk('jobs/createPosition', async (positionData) => {
    const params = {
        url: API.POSITIONS_CREATE.url,
        method: API.POSITIONS_CREATE.type,
        reqBody: positionData
    }
    const response = await Api(params)
    return response?.data || response || {}
})

export const updatePosition = createAsyncThunk('jobs/updatePosition', async (positionData) => {
    const params = {
        url: API.POSITIONS_UPDATE.url,
        method: API.POSITIONS_UPDATE.type,
        reqBody: positionData
    }
    const response = await Api(params)
    return response?.data || response || {}
})

export const deletePosition = createAsyncThunk('jobs/deletePosition', async (positionLokerId) => {
    const params = {
        url: API.POSITIONS_DELETE.url,
        method: API.POSITIONS_DELETE.type,
        reqBody: { position_loker_id: positionLokerId }
    }
    await Api(params)
    return positionLokerId
})

const initialState = {
    jobs: [],
    pagination: {
        total: 0,
        perPage: 10,
        currentPage: 1,
        lastPage: 1
    },
    locations: [],
    cities: [],
    skills: [],
    experienceLevels: [],
    educationLevels: [],
    isLoading: false,
    isMasterDataLoading: 0,
    isSkillLoading: false,
    error: null,
    success: false,
    selectedJob: null,
    jobApplicants: [],
    jobApplicantsPagination: { total: 0, perPage: 20, currentPage: 1, lastPage: 1 },
    isApplicantsLoading: false,
    interviewMap: {},
    isInterviewsLoading: false,
    interviewDetail: null,
    isDetailLoading: false,
    jobInterviewSummary: null,
    isSummaryLoading: false,
    positions: [],
    isPositionLoading: false,
}

export const jobSlice = createSlice({
    name: 'jobs',
    initialState,
    reducers: {
        resetSuccess: (state) => {
            state.success = false
        },
        setSelectedJob: (state, action) => {
            state.selectedJob = action.payload
        },
        resetSelectedJob: (state) => {
            state.selectedJob = null
        }
    },
    extraReducers: (builder) => {
        // Helper: strip double-JSON-encoded strings from backend
        const safeStr = (val) => {
            if (typeof val !== 'string') return val
            try {
                const parsed = JSON.parse(val)
                return typeof parsed === 'string' ? parsed : val
            } catch {
                return val
            }
        }
        const sanitizeJob = (job) => {
            if (!job) return job
            return {
                ...job,
                description: safeStr(job.description),
                responsibilities: safeStr(job.responsibilities),
                requirements: safeStr(job.requirements),
                benefits: safeStr(job.benefits),
            }
        }

        // Fetch Jobs
        builder.addCase(fetchJobs.pending, (state) => {
            state.isLoading = true
            state.error = null
        })
        builder.addCase(fetchJobs.fulfilled, (state, action) => {
            state.isLoading = false
            // Support response with data & pagination
            if (Array.isArray(action.payload?.data)) {
                state.jobs = action.payload.data.map(sanitizeJob)
                state.pagination = action.payload.pagination || state.pagination
            } else if (Array.isArray(action.payload)) {
                state.jobs = action.payload.map(sanitizeJob)
            } else {
                state.jobs = []
            }
        })
        builder.addCase(fetchJobs.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
            state.jobs = []
        })

        // Fetch Locations
        builder.addCase(fetchLocations.pending, (state) => {
            state.isMasterDataLoading++
        })
        builder.addCase(fetchLocations.fulfilled, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            if (Array.isArray(action.payload)) {
                state.locations = action.payload
            } else if (Array.isArray(action.payload?.data)) {
                state.locations = action.payload.data
            } else {
                state.locations = []
            }
        })
        builder.addCase(fetchLocations.rejected, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            state.error = action.error.message
            state.locations = []
        })

        // Fetch Cities for Job Opening location selection
        builder.addCase(fetchCities.pending, (state) => {
            state.isMasterDataLoading++
        })
        builder.addCase(fetchCities.fulfilled, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            if (Array.isArray(action.payload)) {
                state.cities = action.payload
            } else if (Array.isArray(action.payload?.data)) {
                state.cities = action.payload.data
            } else {
                state.cities = []
            }
        })
        builder.addCase(fetchCities.rejected, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            state.error = action.error.message
            state.cities = []
        })

        // Fetch Skills
        builder.addCase(fetchSkills.pending, (state) => {
            state.isMasterDataLoading++
        })
        builder.addCase(fetchSkills.fulfilled, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            if (Array.isArray(action.payload)) {
                state.skills = action.payload
            } else if (Array.isArray(action.payload?.data)) {
                state.skills = action.payload.data
            } else {
                state.skills = []
            }
        })
        builder.addCase(fetchSkills.rejected, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            state.error = action.error.message
            state.skills = []
        })

        // Fetch Experience Levels
        builder.addCase(fetchExperienceLevels.pending, (state) => {
            state.isMasterDataLoading++
        })
        builder.addCase(fetchExperienceLevels.fulfilled, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            if (Array.isArray(action.payload)) {
                state.experienceLevels = action.payload
            } else if (Array.isArray(action.payload?.data)) {
                state.experienceLevels = action.payload.data
            } else {
                state.experienceLevels = []
            }
        })
        builder.addCase(fetchExperienceLevels.rejected, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            state.error = action.error.message
            state.experienceLevels = []
        })

        // Fetch Education Levels
        builder.addCase(fetchEducationLevels.pending, (state) => {
            state.isMasterDataLoading++
        })
        builder.addCase(fetchEducationLevels.fulfilled, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            if (Array.isArray(action.payload)) {
                state.educationLevels = action.payload
            } else if (Array.isArray(action.payload?.data)) {
                state.educationLevels = action.payload.data
            } else {
                state.educationLevels = []
            }
        })
        builder.addCase(fetchEducationLevels.rejected, (state, action) => {
            state.isMasterDataLoading = Math.max(0, state.isMasterDataLoading - 1)
            state.error = action.error.message
            state.educationLevels = []
        })

        // Create Job
        builder.addCase(createJob.pending, (state) => {
            state.isLoading = true
            state.error = null
            state.success = false
        })
        builder.addCase(createJob.fulfilled, (state, action) => {
            state.isLoading = false
            state.success = true
            const newJob = action.payload?.data || action.payload
            if (newJob && newJob.id) {
                state.jobs.push(newJob)
            }
        })
        builder.addCase(createJob.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
            state.success = false
        })

        // Update Job
        builder.addCase(updateJob.pending, (state) => {
            state.isLoading = true
            state.error = null
            state.success = false
        })
        builder.addCase(updateJob.fulfilled, (state, action) => {
            state.isLoading = false
            state.success = true
            const updatedJob = action.payload?.data || action.payload
            if (updatedJob && updatedJob.id) {
                const index = state.jobs.findIndex(job => job.id === updatedJob.id)
                if (index !== -1) {
                    state.jobs[index] = updatedJob
                } else {
                    state.jobs.push(updatedJob)
                }
            }
        })
        builder.addCase(updateJob.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
            state.success = false
        })

        // Delete Job
        builder.addCase(deleteJob.pending, (state) => {
            state.isLoading = true
            state.error = null
            state.success = false
        })
        builder.addCase(deleteJob.fulfilled, (state, action) => {
            state.isLoading = false
            state.success = true
            if (action.payload) {
                state.jobs = state.jobs.filter(job => job.id !== action.payload)
            }
        })
        builder.addCase(deleteJob.rejected, (state, action) => {
            state.isLoading = false
            state.error = action.error.message
            state.success = false
        })

        // Create Skill
        builder.addCase(createSkill.pending, (state) => {
            state.isSkillLoading = true
        })
        builder.addCase(createSkill.fulfilled, (state, action) => {
            state.isSkillLoading = false
            if (action.payload?.id) {
                state.skills.push(action.payload)
            }
        })
        builder.addCase(createSkill.rejected, (state, action) => {
            state.isSkillLoading = false
            state.error = action.error.message
        })

        // Update Skill
        builder.addCase(updateSkill.pending, (state) => {
            state.isSkillLoading = true
        })
        builder.addCase(updateSkill.fulfilled, (state, action) => {
            state.isSkillLoading = false
            const updated = action.payload
            if (updated?.id) {
                const idx = state.skills.findIndex(s => s.id === updated.id)
                if (idx !== -1) state.skills[idx] = updated
            }
        })
        builder.addCase(updateSkill.rejected, (state, action) => {
            state.isSkillLoading = false
            state.error = action.error.message
        })

        // Delete Skill
        builder.addCase(deleteSkill.pending, (state) => {
            state.isSkillLoading = true
        })
        builder.addCase(deleteSkill.fulfilled, (state, action) => {
            state.isSkillLoading = false
            state.skills = state.skills.filter(s => s.id !== action.payload)
        })
        builder.addCase(deleteSkill.rejected, (state, action) => {
            state.isSkillLoading = false
            state.error = action.error.message
        })

        // Fetch Job Applicants
        builder.addCase(fetchJobApplicants.pending, (state) => {
            state.isApplicantsLoading = true
        })
        builder.addCase(fetchJobApplicants.fulfilled, (state, action) => {
            state.isApplicantsLoading = false
            const payload = action.payload
            if (payload?.data?.data) {
                state.jobApplicants = payload.data.data
                state.jobApplicantsPagination = {
                    total: payload.data.total || 0,
                    perPage: payload.data.perPage || 20,
                    currentPage: payload.data.page || 1,
                    lastPage: payload.data.lastPage || 1
                }
            } else {
                state.jobApplicants = []
            }
        })
        builder.addCase(fetchJobApplicants.rejected, (state) => {
            state.isApplicantsLoading = false
            state.jobApplicants = []
        })

        // Fetch Interview Map by Outsource IDs
        builder.addCase(fetchInterviewByOutsourceIds.pending, (state) => {
            state.isInterviewsLoading = true
        })
        builder.addCase(fetchInterviewByOutsourceIds.fulfilled, (state, action) => {
            state.isInterviewsLoading = false
            state.interviewMap = action.payload || {}
        })
        builder.addCase(fetchInterviewByOutsourceIds.rejected, (state) => {
            state.isInterviewsLoading = false
            state.interviewMap = {}
        })

        // Fetch Interview Detail (admin)
        builder.addCase(fetchInterviewDetail.pending, (state) => {
            state.isDetailLoading = true
            state.interviewDetail = null
        })
        builder.addCase(fetchInterviewDetail.fulfilled, (state, action) => {
            state.isDetailLoading = false
            state.interviewDetail = action.payload
        })
        builder.addCase(fetchInterviewDetail.rejected, (state) => {
            state.isDetailLoading = false
            state.interviewDetail = null
        })

        // Fetch Job Interview Summary
        builder.addCase(fetchJobInterviewSummary.pending, (state) => {
            state.isSummaryLoading = true
            state.jobInterviewSummary = null
        })
        builder.addCase(fetchJobInterviewSummary.fulfilled, (state, action) => {
            state.isSummaryLoading = false
            state.jobInterviewSummary = action.payload
        })
        builder.addCase(fetchJobInterviewSummary.rejected, (state) => {
            state.isSummaryLoading = false
            state.jobInterviewSummary = null
        })

        // Fetch Positions (position_lokers)
        builder.addCase(fetchPositions.pending, (state) => {
            state.isPositionLoading = true
        })
        builder.addCase(fetchPositions.fulfilled, (state, action) => {
            state.isPositionLoading = false
            if (Array.isArray(action.payload)) {
                state.positions = action.payload
            } else if (Array.isArray(action.payload?.data)) {
                state.positions = action.payload.data
            } else {
                state.positions = []
            }
        })
        builder.addCase(fetchPositions.rejected, (state) => {
            state.isPositionLoading = false
            state.positions = []
        })

        // Create Position
        builder.addCase(createPosition.pending, (state) => {
            state.isPositionLoading = true
        })
        builder.addCase(createPosition.fulfilled, (state, action) => {
            state.isPositionLoading = false
            const created = action.payload
            if (created?.position_loker_id) {
                state.positions.push(created)
            }
        })
        builder.addCase(createPosition.rejected, (state) => {
            state.isPositionLoading = false
        })

        // Update Position
        builder.addCase(updatePosition.pending, (state) => {
            state.isPositionLoading = true
        })
        builder.addCase(updatePosition.fulfilled, (state, action) => {
            state.isPositionLoading = false
            const updated = action.payload
            if (updated?.position_loker_id) {
                const idx = state.positions.findIndex(p => p.position_loker_id === updated.position_loker_id)
                if (idx !== -1) state.positions[idx] = updated
            }
        })
        builder.addCase(updatePosition.rejected, (state) => {
            state.isPositionLoading = false
        })

        // Delete Position
        builder.addCase(deletePosition.pending, (state) => {
            state.isPositionLoading = true
        })
        builder.addCase(deletePosition.fulfilled, (state, action) => {
            state.isPositionLoading = false
            state.positions = state.positions.filter(p => p.position_loker_id !== action.payload)
        })
        builder.addCase(deletePosition.rejected, (state) => {
            state.isPositionLoading = false
        })
    }
})

export const { resetSuccess, setSelectedJob, resetSelectedJob } = jobSlice.actions
export default jobSlice.reducer
